<template>
	<Popper
		v-if="!$store.loadingUser && !$store.loadingUserWorkspaces && $store.isUserWorkspaceMember"
		v-click-outside="closePopover"
		data-test="workspace-switcher"
		:isVisible="popoverVisible"
		placement="bottom-start"
		@keyup.escape="closePopover"
	>
		<button
			:class="{'popover-trigger-active': popoverVisible}"
			class="workspaces-popover-trigger pr-1"
			@click="popoverVisible = !popoverVisible"
		>
			<WorkspaceAvatar
				:name="$store.workspace.name"
				class="mr-12"
				:colorClass="$store.primaryWorkspaceGravatarColor ? 'bg-red' : 'bg-green'"
				:isBrowserstackWorkspace="$store.isBrowserstackWorkspace"
			/>
			<span :title="workspaceName" data-test="current-selected-workspace-name" class="truncate">
				{{ workspaceName }}
			</span>
			<BaseChevron :flip="popoverVisible" class="ml-auto mr-6" />
		</button>

		<template #popup>
			<BaseTooltip
				:enabled="!$store.isUserAllowedToCreateWorkspace && isCreateWorkspaceTooltipVisible"
				placement="right-end"
			>
				<BasePopover title="Workspaces" class="workspaces-popover -mt-4 bg-gray-700">
					<button
						v-for="{workspace} in $store.userWorkspaces"
						:key="workspace.id"
						class="popover-link text-gray-100"
						@click="switchToWorkspace(workspace.id)"
					>
						<WorkspaceAvatar :name="workspace.name" class="mr-12" colorClass="bg-gray-500" />
						<span :title="workspace.name" class="truncate">
							{{ workspace.name }}
						</span>
						<BaseIcon
							v-if="workspace.id === $store.workspace.id"
							ref="selectedWorkspaceIcon"
							name="mono/check"
							class="ml-auto"
						/>
					</button>
					<template #footer>
						<template v-if="!$store.isBrowserstackUser">
							<RouterLink class="text-gray-100 popover-link mt-4" to="/workspaces/join">
								Join a workspace
							</RouterLink>
							<div
								@mouseenter="isCreateWorkspaceTooltipVisible = true"
								@mouseleave="isCreateWorkspaceTooltipVisible = false"
								@focus="isCreateWorkspaceTooltipVisible = true"
								@blur="isCreateWorkspaceTooltipVisible = false"
							>
								<RouterLink
									v-if="$store.isUserAllowedToCreateWorkspace"
									to="/workspaces/create"
									class="text-gray-100 popover-link"
								>
									Create a workspace
								</RouterLink>
								<span v-else class="popover-link text-gray-400 hover:bg-transparent cursor-not-allowed">
									Create a workspace
								</span>
							</div>
						</template>
						<RouterLink
							v-if="!$route.name.includes('settings')"
							class="text-gray-100 popover-link"
							data-test="settings-link"
							:to="`/workspaces/${$store.workspace.id}/settings`"
						>
							Workspace settings
						</RouterLink>
					</template>
				</BasePopover>
				<template #text>
					You reached the limit of two
					<br />
					trial workspaces per user,
					<br />
					upgrade your plan to create more
				</template>
			</BaseTooltip>
		</template>
	</Popper>
	<router-link
		v-else-if="!$store.loadingUser && !$store.loadingUserWorkspaces && !$store.isUserWorkspaceMember"
		to="/"
		exact
		class="popover-trigger"
	>
		<BaseIcon name="mono/return" class="mr-8 mb-1" />
		Return To My Workspace
	</router-link>
	<BaseLogotype v-else class="bird-icon" />
</template>

<script>
import analytics from 'shared/utils/analytics'
import Popper from 'shared/components/Popper.vue'
import WorkspaceAvatar from 'shared/components/WorkspaceAvatar.vue'

export default {
	components: {
		Popper,
		WorkspaceAvatar,
	},
	data() {
		return {
			popoverVisible: false,
			isCreateWorkspaceTooltipVisible: false,
		}
	},
	computed: {
		workspaceName() {
			return this.$store.workspace.name || ''
		},
		workspaceInitial() {
			if (!this.workspaceName) return ''
			return this.workspaceName[0].toUpperCase()
		},
		destinationRouteName() {
			const currentRouteName = this.$router.currentRoute.value.name
			const isProfileSettingsPage = currentRouteName === 'account-profile-settings'
			if (isProfileSettingsPage) return 'workspace-general-settings'
			// if switching from a session detail view, go to dashboard
			if (currentRouteName.includes('session')) return 'dashboard'
			// if switching from a blocked view, go to dashboard
			if (currentRouteName.includes('blocked')) return 'workspace'
			return currentRouteName
		},
	},
	watch: {
		async popoverVisible(popoverVisible) {
			if (!popoverVisible) return
			await this.$nextTick()
			// scroll the currently selected workspace into view
			this.$refs.selectedWorkspaceIcon?.[0]?.$el?.scrollIntoView({block: 'center'})
		},
	},
	methods: {
		closePopover() {
			this.popoverVisible = false
		},
		switchToWorkspace(workspaceId) {
			analytics.track('Switch Workspace Button Clicked', {from: this.$router.currentRoute.value.name})
			if (workspaceId !== this.$store.workspace.id) {
				this.$routerHelpers.push({
					name: this.destinationRouteName,
					params: {workspaceId},
				})
				this.$store.primaryWorkspaceGravatarColor = !this.$store.primaryWorkspaceGravatarColor
			}
			this.closePopover()
		},
		openAccountSettings() {
			analytics.track('Account Settings Button Clicked')
			this.closePopover()
			this.$routerHelpers.navigateToAccountSettings()
		},
		logout() {
			analytics.track('Logout Button Clicked')
			this.closePopover()
			this.$auth.logout({logoutOfAllTargets: true})
		},
	},
}
</script>

<style scoped>
.workspaces-popover-trigger {
	padding-left: 8px;
	display: flex;
	align-items: center;
	color: var(--gray-100);
	font-weight: 500;
	border-radius: 4px;
	height: 32px;
}

.on-light-background .workspaces-popover-trigger {
	color: var(--gray-100);
	background-color: inherit !important;
}

.dark .on-light-background .workspaces-popover-trigger {
	color: var(--gray-900);
}

.workspaces-popover-trigger:hover,
.workspaces-popover-trigger:active {
	background-color: var(--gray-650);
}

.on-light-background .workspaces-popover-trigger:hover,
.on-light-background .workspaces-popover-trigger:active {
	border: 0.5px solid var(--gray-400);
}

.workspaces-popover-trigger,
.workspaces-popover {
	width: 208px;
}

.bird-icon {
	line-height: 32px;
	margin-left: 8px;
}

:deep(.popover-heading) {
	background-color: var(--gray-700);
}
</style>
